import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { toast } from "react-toastify";
import { FiEye, FiEyeOff, FiLock } from "react-icons/fi";
import { useTranslation } from "react-i18next";

import { changePasswordRequest } from "../../../services/auth";
import styles from "./ChangePasswordSection.module.css";

const ChangePasswordSection = () => {
    const { t } = useTranslation();
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [error, setError] = useState("");

    const schema = useMemo(
        () =>
            yup.object().shape({
                currentPassword: yup.string().required(t("validation.passwordRequired")),
                newPassword: yup
                    .string()
                    .required(t("validation.passwordRequired"))
                    .min(8, t("validation.passwordMinLength"))
                    .max(20, t("validation.passwordMaxLength"))
                    .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/, t("validation.passwordComplexity")),
                confirmPassword: yup
                    .string()
                    .required(t("validation.passwordRequired"))
                    .oneOf([yup.ref("newPassword"), null], t("validation.passwordsMustMatch")),
            }),
        [t]
    );

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting },
    } = useForm({ resolver: yupResolver(schema), mode: "onBlur" });

    const onSubmit = async (values) => {
        setError("");
        try {
            await changePasswordRequest(values.currentPassword, values.newPassword);
            toast.success(t("toasts.passwordChanged"), { toastId: "change-password-success" });
            reset();
        } catch (err) {
            const msg =
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                err.message ||
                t("settings.changePasswordError", { defaultValue: "Şifrəni dəyişmək alınmadı" });
            setError(msg);
        }
    };

    const fields = [
        { name: "currentPassword", label: t("settings.currentPassword"), show: showCurrent, setShow: setShowCurrent },
        { name: "newPassword", label: t("settings.newPassword"), show: showNew, setShow: setShowNew },
        { name: "confirmPassword", label: t("settings.confirmNewPassword"), show: showConfirm, setShow: setShowConfirm },
    ];

    return (
        <section className={styles.section}>
            <div className={styles.sectionTitleRow}>
                <FiLock className={styles.sectionIcon} />
                <h2 className={styles.sectionTitle}>{t("settings.changePassword")}</h2>
            </div>
            <p className={styles.sectionDescription}>{t("settings.changePasswordDescription")}</p>

            <form onSubmit={handleSubmit(onSubmit)} className={styles.form} noValidate>
                {error && <p className={styles.error}>{error}</p>}

                {fields.map((f) => (
                    <div key={f.name} className={styles.formGroup}>
                        <label className={styles.label}>{f.label}</label>
                        <div className={styles.passwordWrap}>
                            <input
                                type={f.show ? "text" : "password"}
                                autoComplete={f.name === "currentPassword" ? "current-password" : "new-password"}
                                {...register(f.name)}
                                className={styles.input}
                            />
                            <button
                                type="button"
                                className={styles.passwordToggle}
                                onClick={() => f.setShow((v) => !v)}
                                aria-label={f.show ? t("auth.hidePassword") : t("auth.showPassword")}
                            >
                                {f.show ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                            </button>
                        </div>
                        {errors[f.name] && <p className={styles.error}>{errors[f.name].message}</p>}
                    </div>
                ))}

                <div className={styles.actions}>
                    <button type="submit" disabled={isSubmitting} className={styles.submitBtn}>
                        {isSubmitting ? t("common.saving") : t("settings.changePassword")}
                    </button>
                </div>
            </form>
        </section>
    );
};

export default ChangePasswordSection;
